"use client";
import React, { useState, useEffect, useCallback } from "react";
import { ButtonPrimary } from "@/components/common/ButtonPrimary";
import { ButtonOutline } from "@/components/common/ButtonOutline";
import { Button } from "@/components/ui/button";
import {
  Search,
  ArrowLeft,
  Layout,
  LoaderCircle,
  Sparkles,
  PlusCircle,
} from "lucide-react";
import { Template } from "@/types/models/template";
import { TemplateService } from "@/services/templateService";
import { generateImageSrc } from "@/utils/helper";

interface TemplateBrowserProps {
  onBack: () => void;
  onSelectTemplate: (templateId: string, templateName: string) => void;
  onCreateBlank: () => void;
  /** Compact layout for embedding in dialogs (no full-page chrome). */
  embedded?: boolean;
}

export function TemplateBrowser({
  onBack,
  onSelectTemplate,
  onCreateBlank,
  embedded = false,
}: TemplateBrowserProps) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingTemplate, setLoadingTemplate] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await TemplateService.getTemplates(1, 20, searchTerm);
      console.log("Templates fetched:", response.data);
      setTemplates(response.data);
    } catch (err) {
      console.error("Failed to fetch templates:", err);
      setError("Failed to load templates. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSelectTemplate = async (template: Template) => {
    try {
      console.log("Template being selected:", template);
      setLoadingTemplate(template.id);
      setSelectedTemplate(template.id);
      setError(null);

      // Call the parent callback with template ID and name for navigation
      onSelectTemplate(template.id, template.name);
    } catch (err) {
      console.error("Error handling template selection:", err);
      setError("Failed to select template. Please try again.");
      setSelectedTemplate(null);
    } finally {
      setLoadingTemplate(null);
    }
  };

  const renderTemplateCard = (template: Template) => {
    const previewImage = template.assets?.[0]?.path;
    const isLoadingThis = loadingTemplate === template.id;
    console.log("Rendering template card:", {
      template,
      previewImage,
      isLoadingThis,
    });
    return (
      <div
        key={template.id}
        className={`bg-card dark:bg-auth-card rounded-lg border border-border dark:border-auth-background overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer group ${
          selectedTemplate === template.id ? "ring-2 ring-primary" : ""
        } ${isLoadingThis ? "opacity-50" : ""}`}
        onClick={() => !isLoadingThis && handleSelectTemplate(template)}
      >
        {/* Template Preview */}
        <div className="relative aspect-[16/10] bg-muted dark:bg-auth-background overflow-hidden">
          {previewImage ? (
            <img
              src={generateImageSrc(previewImage)}
              alt={template.name}
              width={400}
              height={250}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
              onError={(e) => {
                e.currentTarget.style.display = "none";
                e.currentTarget.nextElementSibling?.classList.remove("hidden");
              }}
            />
          ) : null}
          <div
            className={`${previewImage ? "hidden" : ""} absolute inset-0 flex items-center justify-center bg-muted dark:bg-auth-background`}
          >
            <Layout className="w-12 h-12 text-muted-foreground" />
          </div>

          {/* Loading overlay */}
          {isLoadingThis && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <div className="flex items-center space-x-2 text-white">
                <LoaderCircle className="w-5 h-5 animate-spin" />
                <span className="text-sm">Loading...</span>
              </div>
            </div>
          )}

          {/* Overlay on hover */}
          {!isLoadingThis && (
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
              <Button
                variant="default"
                size="default"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  handleSelectTemplate(template);
                }}
                className="transform scale-90 group-hover:scale-100 transition-transform duration-200"
              >
                Use Template
              </Button>
            </div>
          )}
        </div>

        {/* Template Info */}
        <div className="p-4">
          <h3 className="font-semibold text-foreground mb-1 line-clamp-1">
            {template.name}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2">
            {template.description || "No description available"}
          </p>
          <div className="mt-2 text-xs text-muted-foreground">
            {template.total_pages || 0} page
            {template.total_pages === 1 ? "" : "s"}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={
        embedded ? "" : "h-full w-full bg-gradient-page dark:bg-gradient-page-dark"
      }
    >
      <div className={embedded ? "space-y-4" : "max-w-7xl mx-auto px-8 py-12"}>
        {/* Header */}
        <div
          className={
            embedded
              ? "flex items-center gap-3"
              : "flex items-center justify-between mb-8 animate-fadeInUp"
          }
        >
          <div className="flex items-center space-x-4">
            <button
              type="button"
              onClick={onBack}
              className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className={embedded ? "w-4 h-4" : "w-5 h-5"} />
              {!embedded && <span>Back</span>}
            </button>
            {!embedded && (
              <>
                <div className="w-px h-6 bg-border"></div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground">
                    Choose a Template
                  </h1>
                  <p className="text-muted-foreground">
                    Start with a professionally designed template or create a blank
                    page
                  </p>
                </div>
              </>
            )}
            {embedded && (
              <div>
                <h2 className="text-base font-semibold text-foreground">
                  Browse templates
                </h2>
                <p className="text-xs text-muted-foreground">
                  Tap a template to continue
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Search and Blank Page Option */}
        <div
          className={embedded ? "" : "mb-8 animate-fadeInUp"}
          style={embedded ? undefined : { animationDelay: "0.1s" }}
        >
          {/* Search */}
          <div className="max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-background border border-border dark:border-auth-background rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div
          className={
            embedded
              ? "max-h-[min(52vh,420px)] overflow-y-auto pr-1 -mr-1"
              : "animate-fadeInUp"
          }
          style={embedded ? undefined : { animationDelay: "0.2s" }}
        >
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex items-center space-x-3 text-muted-foreground">
                <LoaderCircle className="w-6 h-6 animate-spin" />
                <span>Loading templates...</span>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 max-w-md mx-auto">
                <h3 className="font-semibold text-destructive mb-2">
                  Error Loading Templates
                </h3>
                <p className="text-sm text-muted-foreground mb-4">{error}</p>
                <ButtonPrimary
                  onClick={fetchTemplates}
                  className="bg-destructive hover:bg-destructive/90"
                >
                  Try Again
                </ButtonPrimary>
              </div>
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-20">
              <div className="bg-card dark:bg-auth-card border border-border dark:border-auth-background rounded-lg p-8 max-w-md mx-auto">
                <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold text-foreground mb-2">
                  {searchTerm ? "No templates found" : "No templates available"}
                </h3>
                <p className="text-sm text-muted-foreground mb-6">
                  {searchTerm
                    ? `No templates match "${searchTerm}". Try a different search term.`
                    : "It looks like there are no templates available yet."}
                </p>
                {searchTerm ? (
                  <ButtonOutline onClick={() => setSearchTerm("")}>
                    Clear Search
                  </ButtonOutline>
                ) : (
                  <ButtonPrimary onClick={onCreateBlank}>
                    <PlusCircle className="w-4 h-4 mr-2" />
                    Create Blank Page
                  </ButtonPrimary>
                )}
              </div>
            </div>
          ) : (
            <div
              className={
                embedded
                  ? "grid grid-cols-1 sm:grid-cols-2 gap-3"
                  : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              }
            >
              {templates.map(renderTemplateCard)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
